import React from 'react';
import { Collapse } from 'antd';
const { Panel } = Collapse;

/**
 * M3PInfo:
 * Informational page about Medicare Prescription Payment Plan (M3P),
 * including an overview, key highlights, and a FAQ section.
 */
export default function M3PInfo() {
  return (
    <div style={{ padding: '24px', maxWidth: '1000px', margin: 'auto', lineHeight: '1.6' }}>
      <h1 style={{ fontSize: '28px', marginBottom: '1rem' }}>
      Overview &amp; FAQs: Medicare Prescription Payment Plan (M3P)
      </h1>

      {/* OVERVIEW SECTION */}
      <section style={{ marginBottom: '2rem' }}>
        <p>
          Starting January 1, 2025, all Medicare prescription drug plans must offer the 
          <strong> Medicare Prescription Payment Plan (M3P)</strong>. This payment program, 
          established by the Inflation Reduction Act (IRA), allows Medicare patients to manage 
          their prescription drug costs through affordable, <strong>capped monthly payments</strong> 
          instead of paying large lump sums at the pharmacy.
        </p>

        <h3 style={{ marginTop: '1.5rem' }}>Who is Eligible?</h3>
        <p>
          <strong>All Medicare Part D patients</strong>, including those enrolled in 
          Prescription Drug Plans (PDP) and Medicare Advantage Prescription Drug Plans (MAPD).
          M3P applies at all pharmacy types.
        </p>

        <h3 style={{ marginTop: '1.5rem' }}>What Does M3P Offer?</h3>
        <p>
          <strong>No upfront pharmacy costs:</strong> Participants pay $0 at the pharmacy for 
          covered medications.<br />
          <strong>Monthly billing:</strong> Prescription costs are billed monthly by the patient's 
          Medicare Part D plan.
        </p>

        <h3 style={{ marginTop: '1.5rem' }}>What M3P Does NOT Do:</h3>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Save money on total prescription costs.</li>
          <li>Change how patients progress through Medicare coverage phases.</li>
          <li>
            Alter what counts towards the True Out-of-Pocket (TrOOP) spending 
            maximum (<strong>$2,000</strong>).
          </li>
        </ul>

        <h3 style={{ marginTop: '1.5rem' }}>How Does M3P Work?</h3>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>Patients must opt-in to participate.</li>
          <li>
            Patients pay $0 at the pharmacy for covered medications 
            (<em>the pharmacy itself doesn't calculate M3P cost at checkout</em>).
          </li>
          <li>
            Part D plan sponsors will <strong>bill patients monthly</strong> for cost-sharing 
            related to medication claims.
          </li>
          <li>
            Pharmacies are reimbursed by the Part D plan sponsor; the patient pays 
            the plan, not the pharmacy, under M3P.
          </li>
        </ul>

        <h3 style={{ marginTop: '1.5rem' }}>Who Benefits Most?</h3>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>
            Patients with large co-pays or co-insurance who cannot pay their total amount in a single 
            transaction (e.g., big deductibles).
          </li>
          <li>
            Patients with a cost-share of <strong>≥$600</strong> for a single prescription will 
            automatically be identified by Part D sponsors and informed about the M3P option.
          </li>
        </ul>

        <h3 style={{ marginTop: '1.5rem' }}>How to Opt In to M3P?</h3>
        <ul style={{ paddingLeft: '1.5rem' }}>
          <li>
            <strong>Patients must contact their Medicare plan</strong> via phone, paper form, 
            or online portal to opt in.
          </li>
          <li>Pharmacies cannot enroll patients in M3P at the counter.</li>
          <li>
            Patients typically receive confirmation from the insurance plan within 24 hours after 
            submitting an opt-in request.
          </li>
        </ul>
      </section>

      {/* FORMULAS SECTION */}
      <section style={{ marginBottom: '2rem' }}>
        <h2 style={{ fontSize: '22px', marginBottom: '1rem' }}>
          Formulas for Monthly Payments in M3P
        </h2>
        <p>
          The monthly bill in M3P is <strong>capped</strong> each month to ensure patients 
          don’t face unaffordable large sums at once. Below are the key formulas:
        </p>

        <div style={{ border: '1px solid #ccc', borderRadius: '5px', padding: '1rem', marginBottom: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>First Month Maximum Cap Bill</h3>
          <p style={{ margin: 0 }}>
            <em>
              [ Annual OOP Threshold ($2,000 in 2025) 
              − any prescription costs the patient has already paid out of pocket that count toward TrOOP]
              /
              [ Number of months remaining in the year]
            </em>
          </p>
        </div>

        <div style={{ border: '1px solid #ccc', borderRadius: '5px', padding: '1rem' }}>
          <h3 style={{ marginTop: 0 }}>Subsequent Month Maximum Cap Bill</h3>
          <p style={{ margin: 0 }}>
            <em>
              [ Current M3P OOP Remaining Balance Due 
              + (New OOP Incurred Cost in the Month)]
              / 
              [ Number of months remaining in the year]
            </em>
          </p>
        </div>
      </section>
      <section style={{ marginTop: '32px' }}>
        <h2>Frequently Asked Questions (FAQs)</h2>
        <Collapse accordion>
          <Panel header="How is the M3P bill paid? Do patients receive a monthly statement, or is it taken out of Social Security?" key="1">
            <p>
              <ul>
                <li>Participants in the Medicare Prescription Payment Plan (M3P) receive a monthly standalone billing statement. This statement includes: Itemized out-of-pocket (OOP) costs by prescription for the month billed; Balances carried over from prior months and missed payments and plan contact information.</li>
                <li>CMS encourages flexibility, allowing payments via electronic funds transfer (EFT), credit/debit cards, or checks. However, cash payments are not required to be accepted due to concerns about theft and consistency with premium payment options.</li>
                <li>M3P payments are not automatically deducted from Social Security benefits. Participants must pay directly through the methods provided by the Part D sponsor.</li>
              </ul>
            </p>
          </Panel>
          <Panel header="What happens if a patient defaults on payments? How will this affect their ability to fill prescriptions at the pharmacy?" key="2">
            <p>
              <ul>
                <li>If a participant defaults on payments, their ability to fill prescriptions at the pharmacy remains unaffected as long as they pay premiums for prescription coverage. CMS guidance prohibits Part D sponsors from denying access to new or existing medications due to unpaid balances.</li>
                <li>Sponsors will continue billing participants for unpaid amounts. Aggressive debt collection practices are prohibited, and sponsors must comply with all federal and state debt collection laws.</li>
                <li>Participants can continue paying their balances in monthly installments or as a lump sum, with no immediate repercussions on their coverage or prescription access.</li>
              </ul>
            </p>
          </Panel>
          <Panel header="If a patient has a late payment, will there be any late fees or penalties?" key="3">
            <p>
              <li>No Late Fees or Penalties: CMS explicitly prohibits Part D sponsors from charging participants late fees, interest, or other penalties related to missed or late payments. This prohibition extends to third-party contractors. CMS requires sponsors to reconcile any billing inaccuracies promptly. Participants must not incur additional charges (e.g., overdraft fees) due to sponsor errors.</li>
            </p>
          </Panel>
          <Panel header="How can pharmacies and healthcare providers confirm whether a patient is enrolled in the M3P program?" key="4">
            <p>
              Healthcare providers and pharmacies can verify a patient’s enrollment by contacting the Part D sponsor directly. Sponsors have access to real-time enrollment information and can confirm participation status. Pharmacies and healthcare providers can confirm a patient’s enrollment in M3P through the claims processing system. Enrollment is identified using a unique Bank Identification Number (BIN) and/or Processor Control Number (PCN) specific to the M3P program. These identifiers are used at the pharmacy point-of-sale (POS) to determine if a participant is in the program
            </p>
          </Panel>
          <Panel header="Is M3P enrollment available 24 hours a day, 365 days a year, or only on weekdays?" key="5">
            <p>
            M3P enrollment is available 24 hours a day, 365 days a year. This ensures that participants can opt into the program at any time during the year without being restricted to business hours. While enrollment is available at any time, the processing of the opt-in request typically takes 24 hours. Once processed, the enrollment becomes effective at the start of the next calendar month.
            </p>
          </Panel>

          <Panel header="If a patient’s out-of-pocket cost is expected to exceed $2,000, is it better for them to avoid enrolling in M3P so they can reach catastrophic coverage sooner? How does spreading the $2,000 cost over 12 months impact their eligibility for catastrophic coverage?" key="6">
            <p>
            Whether enrolling in M3P does not affect the coverage stage, M3P just allows the patient to spread the $2,000 cost over monthly installments, but the full cost still counts toward the TrOOP as it is incurred, not when it is paid. This ensures that participation in M3P does not delay catastrophic coverage eligibility. Spreading $2,000 cost in M3P did not impact the eligibility for catastrophic coverage.
            </p>
          </Panel>

          <Panel header="Do M3P payments apply only to the $2,000 out-of-pocket maximum? Does the patient need to pay their deductible before becoming eligible for M3P payments?" key="7">
            <p>
            M3P applies only to costs contributing to the $2,000 out-of-pocket maximum, including deductibles, copays, and coinsurance for covered Part D drugs. Patients can enroll in M3P anytime and do not need to pay deductibles before becoming eligible; instead, they if they enroll in M3P, their deductible will also be spread.
            </p>
          </Panel>

          <Panel header="Will insulin products still have a $35 monthly cap under the M3P program?" key="8">
            <p>
            Insulin products covered by Part D plans will retain the $35 monthly cap, and the costs are included towards $2,000 cap. the $35 monthly insulin payment is spread over 12 months as part of M3P.
            </p>
          </Panel>

          <Panel header="Are premiums kept separate from M3P payments? For example, will patients receive two separate bills?" key="9">
            <p>
            Part D premiums are kept entirely separate from M3P payments. Patients will receive two separate bills: One for their monthly M3P payments; One for their plan premiums, if they are not deducted directly from Social Security benefits. 
            </p>
          </Panel>

          <Panel header="If a patient uses a continuous glucose monitor (CGM) covered under Part B, will their copays count toward the $2,000 out-of-pocket maximum or be included in the payment plan?" key="10">
            <p>
            Costs for items covered under Part B, such as continuous glucose monitors (CGMs), do not count toward the $2,000 out-of-pocket cap under M3P. The $2,000 cap applies exclusively to Part D covered drugs, and Part B copays are managed separately.
            </p>
          </Panel>

          <Panel header="Are all pharmacies, including small local pharmacies, required to participate in the M3P program?" key="11">
            <p>
            All network pharmacies associated with Part D sponsors must process claims for patients enrolled in M3P. This includes large chain pharmacies and small local pharmacies within the Part D network.
            Pharmacies must comply with the claims processing requirements set by the Part D sponsor, including using the unique BIN/PCN identifiers for M3P claims.
            </p>
          </Panel>

          <Panel header="How will pharmacies know if a patient is enrolled in the M3P program?" key="12">
            <p>
              Pharmacies use the claims processing system to determine a patient’s M3P enrollment status. M3P-specific BIN/PCN identifiers are transmitted during the claims adjudication process to flag participation. Pharmacies can also contact the Part D sponsor directly to verify a patient’s enrollment if needed
            </p>
          </Panel>


          <Panel header="If a patient enrolls in M3P, does the program apply to all their prescriptions, or can they choose which medications to include?" key="13">
            <p>
              Once a patient enrolls in M3P, the program applies to all their covered Part D drugs. Patients cannot selectively include or exclude specific medications. Non-covered drugs (e.g., non-formulary medications) or Part B drugs are not included in M3P and must be paid for separately.
            </p>
          </Panel>

          <Panel header="Will M3P plan-sponsored monthly payments be considered co-insurance or co-payments? How does this affect state-sponsored programs, such as those in New York, that subsidize co-pays or co-insurance after meeting the Medicare deductible?" key="14">
            <p>
              <ul>
                <li>M3P monthly payments are not directly classified as co-insurance or co-payments. Instead, they are treated as installments toward the patient’s total out-of-pocket (OOP) costs, which include both co-pays and co-insurance for covered Part D medications. For purposes of coordination with state assistance programs, such as those in New York, M3P payments may need to be categorized by the Part D sponsor as contributions toward co-payments or co-insurance when reporting costs.</li>
                <li>Enrolling in M3P may not provide significant benefits to patients receiving subsidies, as the program already helps reduce immediate costs at the point of sale. Furthermore, M3P may introduce logistical complications. The current point-of-sale process simplifies subsidy applications and billing, while M3P’s installment model could make the coordination of benefits with state programs more complex.</li>
                <li>This challenge also extends to 340B clinic pharmacies if patients are eligible for co-pay assistance or foundation grants. Enrolling in M3P is not recommended in such cases, as it can complicate billing and reimbursement processes. For patients benefiting from existing co-pay assistance or subsidies, continuing with the standard payment model may be a more practical option.</li>
              </ul>
            </p>
          </Panel>
        </Collapse>
      </section>
    </div>
  );
}
